import { State } from '@meraki-internal/state';
import { clientDisplayName } from '@innerhive-internal/innerhive-common';
import { BeeChatClient, IChat, IChatMessage } from '../helper-bee/BeeChatClient';
import { stripCitations } from '../helper-bee/stripCitations';
import { BeaChatFormatter } from '../helper-bee/BeaChatFormatter';
import { IBeaConversationState, IBeaConversationStateData, IChatRowSummary } from '../helper-bee/IBeaConversationState';
import type { IBeeChatTrackingFrom } from '../helper-bee/BeeChatViewModel';
import { OrganizationsState } from '../organizations/OrganizationsState';
import { IOrganization } from '../organizations/IOrganization';
import { ILink } from '../caremap/WithLinks';
import { CareMapState } from '../caremap/CareMapState';
import { ICareMapKey } from '../caremap/ICareMap';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { Logger } from '../support/Logger';
import { BeaSelector } from './BeaSelector';

// What the panel says Bea is doing while a caseload tool runs: a caseload-wide read takes seconds,
// and silence under the typing dots reads as broken. Keyed by the api's tool names.
const ACTIVITY_BY_TOOL: Record<string, string> = {
    get_caseload_overview: 'Reading the caseload',
    search_records: 'Searching client records',
    get_record: 'Opening a record',
    get_client_profile: 'Reading a care profile',
    get_timeline: 'Reading the timeline',
    get_appointments: 'Checking appointments',
    get_tasks: 'Checking tasks',
    get_insights: 'Reading findings',
    web_search: 'Searching the web',
    file_search: 'Checking the help docs'
};

/** Where the panel was opened from, for tracking only: a family entry point's name, or an agency tab. */
export type IAgencyBeaSurface = IBeeChatTrackingFrom | 'Clients Tab' | 'Insights Tab' | 'Insights Ask About This';

interface IAgencyActiveClient {
    careMapKey: ICareMapKey;
    name: string;
}

interface IAgencyBeaStateData extends IBeaConversationStateData {
    isOpen: boolean;
    isMinimized: boolean;
    // Whether the shell showing the panel covers the page (a phone's modal), which the panel reports.
    coversPage: boolean;
    organization?: IOrganization;
    // The client on screen, which every question naming nobody is about. Cleared by the person to
    // ask across the caseload instead; set again when they move to another client.
    activeClient?: IAgencyActiveClient;
}

const sameKey = (a: ICareMapKey | undefined, b: ICareMapKey | undefined): boolean => {
    return Boolean(a && b && a.userId === b.userId && a.careMapId === b.careMapId);
};

// The chip above names the client, so the greeting needs no name of its own.
const greetingFor = (activeClient: IAgencyActiveClient | undefined): string => {
    return activeClient
        ? 'Hi, I\'m Bea. Ask me anything about this client, or clear them above to ask about your whole caseload.'
        : 'Hi, I\'m Bea. Ask me about your whole caseload, or about any client by name.';
};

/**
 * Agency Bea's panel and conversation: one chat over the organization's caseload at a time, sent to
 * the stream with the organization's id and the active client, and listed under the organization
 * rather than among the member's family chats.
 */
export class AgencyBeaState extends State<IAgencyBeaStateData> implements IBeaConversationState {
    static inject = () => [BeeChatClient, OrganizationsState, CareMapState, BeaSelector, BeaChatFormatter, MixPanelEventEmitter, Logger];

    private abortController: AbortController | null = null;
    // The client the person cleared the chip for, so moving to another client sets it again.
    private clearedCareMapKey: ICareMapKey | undefined;
    // A chat the api created this session, continued by id until the list reads it back as a resource.
    private pendingChatId: string | undefined;
    // Entry points' messages that arrived mid-answer, sent in order, one per finished answer.
    private queuedMessages: string[] = [];

    constructor(
        private chatClient: BeeChatClient,
        private orgState: OrganizationsState,
        private careMapState: CareMapState,
        private selector: BeaSelector,
        private formatter: BeaChatFormatter,
        private mixpanel: MixPanelEventEmitter,
        private log: Logger
    ) {
        super({
            isOpen: false,
            isMinimized: false,
            coversPage: false,
            chats: [],
            activeChat: null,
            messages: [],
            isSending: false,
            isStreaming: false,
            streamingText: '',
            currentView: 'chat'
        });
    }

    open = (params: { surface: IAgencyBeaSurface; initialMessage?: string }) => {
        const { surface, initialMessage } = params;
        const organization = this.selector.getAgencyOrganization();
        if (!organization) {
            return;
        }
        const sameOrganization = organization.orgId === this.state.organization?.orgId;
        if (this.state.isOpen && sameOrganization) {
            // The dock stays open while the person moves around, so an entry point pressed again
            // only adds its message to the conversation in progress.
            this.sendAfterAnyAnswer(initialMessage);
            return;
        }
        if (this.state.isMinimized && sameOrganization) {
            this.setState({ isOpen: true, isMinimized: false });
        } else {
            this.setState({ isOpen: true, isMinimized: false, organization, chats: [] });
            this.startNewConversation();
            this.loadChats();
        }
        this.clearedCareMapKey = undefined;
        this.syncActiveClient();
        this.mixpanel.track('Agency Bea Opened', () => ({
            'Surface': surface,
            'Has Active Client': Boolean(this.state.activeClient)
        }));
        this.sendAfterAnyAnswer(initialMessage);
    };

    // A message from an entry point waits for the answer under way rather than cutting it off.
    private sendAfterAnyAnswer = (message: string | undefined) => {
        if (!message) {
            return;
        }
        if (this.state.isSending || this.state.isStreaming) {
            // A double tap would otherwise have Bea answer the same question twice.
            const answering = this.state.messages.filter(sent => sent.role === 'user').at(-1)?.content;
            if (message !== answering && message !== this.queuedMessages.at(-1)) {
                this.queuedMessages.push(message);
            }
        } else {
            this.sendMessage(message);
        }
    };

    minimize = () => {
        this.setState({ isOpen: false, isMinimized: true });
    };

    /** The panel reports whether its shell covers the page, which decides what following a link does. */
    setCoversPage = (coversPage: boolean) => {
        if (coversPage !== this.state.coversPage) {
            this.setState({ coversPage });
        }
    };

    /** A client link in an answer was followed: a shell over the page gets out of the record's way. */
    clientLinkFollowed = () => {
        if (this.state.isOpen && this.state.coversPage) {
            this.minimize();
        }
    };

    close = () => {
        this.cancelStream();
        this.setState({ isOpen: false, isMinimized: false });
    };

    /**
     * Keeps the chip on the client the person is looking at as they move between clients. Called by
     * the panel whenever the care map on screen changes; a cleared chip stays cleared for that client.
     */
    syncActiveClient = () => {
        const organization = this.state.organization;
        const careMap = this.selector.getCareMapOnScreen();
        if (!organization || !careMap || !this.selector.isClientOf(organization, careMap)) {
            if (this.state.activeClient) {
                this.setActiveClient(undefined);
            }
            return;
        }
        const careMapKey: ICareMapKey = { userId: careMap.userId, careMapId: careMap.careMapId };
        if (sameKey(careMapKey, this.clearedCareMapKey)) {
            if (this.state.activeClient) {
                this.setActiveClient(undefined);
            }
            return;
        }
        if (sameKey(careMapKey, this.state.activeClient?.careMapKey)) {
            return;
        }
        this.clearedCareMapKey = undefined;
        this.setActiveClient({ careMapKey, name: clientDisplayName(this.careMapState.getPatientFullName(careMap)) });
    };

    clearActiveClient = () => {
        this.clearedCareMapKey = this.state.activeClient?.careMapKey;
        this.setActiveClient(undefined);
    };

    // The greeting follows the scope until the first question; after that it stays as it was.
    private setActiveClient = (activeClient: IAgencyActiveClient | undefined) => {
        const isEmpty = this.conversationOf(this.state.messages).length === 0;
        this.setState({ activeClient, ...(isEmpty ? { messages: this.withGreeting({ conversation: [], activeClient }) } : {}) });
    };

    // Bea's greeting heads every conversation on screen and is never part of the chat itself.
    private withGreeting = (params: { conversation: IChatMessage[]; activeClient?: IAgencyActiveClient }): IChatMessage[] => {
        const { conversation, activeClient } = params;
        const greeting: IChatMessage = { role: 'assistant', content: greetingFor(activeClient), createdAt: conversation[0]?.createdAt ?? new Date().toISOString() };
        return [greeting, ...conversation];
    };

    private conversationOf = (messages: IChatMessage[]): IChatMessage[] => messages.slice(1);

    private loadChats = async () => {
        const organization = this.state.organization;
        if (!organization?.links.chats) {
            return;
        }
        try {
            const cleaned = this.formatter.cleanLoaded(await this.chatClient.getOrganizationChats({ organization }));
            // The chat in progress becomes a listed resource once the list has read it back.
            const continued = this.pendingChatId ? cleaned.find(chat => chat.chatId === this.pendingChatId) : undefined;
            this.setState({ chats: cleaned, ...(continued ? { activeChat: continued } : {}) });
            if (continued) {
                this.pendingChatId = undefined;
            }
        } catch (error) {
            // The list is the panel's history, not the conversation: a failed read leaves the chat usable.
            this.log.error(error, { context: 'agency Bea could not list the organization\'s chats', orgId: organization.orgId });
        }
    };

    startNewConversation = () => {
        this.cancelStream();
        this.pendingChatId = undefined;
        this.queuedMessages = [];
        this.setState({ activeChat: null, messages: this.withGreeting({ conversation: [], activeClient: this.state.activeClient }), currentView: 'chat', isSending: false, isStreaming: false, streamingText: '', activity: undefined });
    };

    selectChat = (chatId: string) => {
        const chat = this.state.chats.find(candidate => candidate.chatId === chatId);
        if (!chat) {
            return;
        }
        if (chatId === (this.state.activeChat?.chatId ?? this.pendingChatId) && this.abortController) {
            // Back to the chat still being answered: the conversation on screen is ahead of the list's copy.
            this.setState({ currentView: 'chat' });
            return;
        }
        this.cancelStream();
        this.pendingChatId = undefined;
        this.queuedMessages = [];
        this.setState({ activeChat: chat, messages: this.withGreeting({ conversation: chat.messages, activeClient: this.state.activeClient }), isSending: false, isStreaming: false, streamingText: '', activity: undefined });
        // Delay allows React render and browser paint to complete before animating
        setTimeout(() => {
            this.setState({ currentView: 'chat' });
        }, 150);
    };

    showConversationList = () => {
        this.setState({ currentView: 'list' });
        // The api writes a new chat's title alongside its first answer, so the list is read again
        // as it opens to show titles that landed after the turn's refresh.
        this.loadChats();
    };

    getTitle = (chat: IChat): string => this.formatter.getTitle(chat);

    getSummary = (chat: IChat): IChatRowSummary => {
        if (!chat.activeCareMapKey) {
            return { text: 'All clients', isPersonName: false };
        }
        const careMap = this.state.organization
            ? this.orgState.getOrganizationCareMaps(this.state.organization.orgId).find(candidate => sameKey(candidate, chat.activeCareMapKey))
            : undefined;
        return careMap
            ? { text: clientDisplayName(this.careMapState.getPatientFullName(careMap)), isPersonName: true }
            : { text: 'One client', isPersonName: false };
    };

    private cancelStream = () => {
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
    };

    sendMessage = async (text: string) => {
        const organization = this.state.organization;
        const streamLink = organization?.links.chatStream;
        if (!organization || !streamLink) {
            return;
        }
        const userMessage: IChatMessage = { role: 'user', content: text, createdAt: new Date().toISOString() };
        const updatedMessages = [...this.state.messages, userMessage];
        // Shown at once: the api reads the caseload, and a scoped turn the client's record, before
        // the model's first call, so its first tool call is seconds away.
        const activity = this.state.activeClient ? 'Reading the client\'s record' : 'Reading the caseload';
        this.setState({ messages: updatedMessages, isSending: true, isStreaming: false, streamingText: '', activity });

        const chatId = this.state.activeChat?.chatId ?? this.pendingChatId;
        this.mixpanel.track('Agency Bea Message Sent', () => ({
            'Has Active Client': Boolean(this.state.activeClient),
            'Message Length': text.length,
            'Is New Chat': !chatId
        }));

        const controller = new AbortController();
        try {
            await this.stream({ organization, streamLink, message: text, chatId, updatedMessages, controller });
        } catch (error) {
            if (controller.signal.aborted) {
                // The person closed the panel or moved on mid-answer; whatever replaced this turn
                // owns the state now.
                return;
            }
            this.log.error(error, { context: 'agency Bea turn failed', orgId: organization.orgId });
            const failure: IChatMessage = { role: 'assistant', content: 'Something went wrong on my side. Please try that again.', createdAt: new Date().toISOString() };
            this.setState({ messages: [...updatedMessages, failure], isSending: false, isStreaming: false, streamingText: '', activity: undefined });
            this.sendQueuedMessage();
        }
    };

    private stream = async (params: { organization: IOrganization; streamLink: ILink; message: string; chatId?: string; updatedMessages: IChatMessage[]; controller: AbortController }) => {
        const { organization, streamLink, message, chatId, updatedMessages, controller } = params;
        const activeClient = this.state.activeClient;

        this.cancelStream();
        this.abortController = controller;
        this.setState({ isSending: false, isStreaming: true, streamingText: '' });

        let streamingText = '';
        let newChatId: string | undefined;
        try {
            for await (const event of this.chatClient.streamChat({
                streamUrl: streamLink.href,
                message,
                chatId,
                orgId: organization.orgId,
                careMapKey: activeClient?.careMapKey,
                signal: controller.signal,
                metadata: { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }
            })) {
                if (event.type === 'chat_created') {
                    newChatId = event.chatId;
                    // Known at once, so a second message or a retry continues this chat rather than
                    // opening another; only while this turn still owns the conversation.
                    if (!chatId && this.abortController === controller) {
                        this.pendingChatId = event.chatId;
                    }
                } else if (event.type === 'tool_call' && event.toolName) {
                    this.setState({ activity: ACTIVITY_BY_TOOL[event.toolName] || 'Looking that up' });
                } else if (event.type === 'text' && event.text) {
                    streamingText += event.text;
                    this.setState({ streamingText: stripCitations(streamingText, true), activity: undefined });
                } else if (event.type === 'done') {
                    this.finishTurn({ chatId: newChatId || event.chatId || chatId, updatedMessages, streamingText });
                } else if (event.type === 'error') {
                    throw new Error(event.message || 'Stream error');
                }
            }
        } finally {
            // A later turn may already own the controller slot.
            if (this.abortController === controller) {
                this.abortController = null;
            }
        }
    };

    private finishTurn = (params: { chatId?: string; updatedMessages: IChatMessage[]; streamingText: string }) => {
        const { chatId, updatedMessages, streamingText } = params;
        const assistantMessage: IChatMessage = {
            role: 'assistant',
            content: stripCitations(streamingText, false) || 'I could not put an answer together just now. Please try again.',
            createdAt: new Date().toISOString()
        };
        const shown = [...updatedMessages, assistantMessage];
        const persisted = this.conversationOf(shown);

        const activeChat = this.state.activeChat;
        if (activeChat && activeChat.chatId === chatId) {
            const updated: IChat = { ...activeChat, messages: persisted, lastMessageAt: assistantMessage.createdAt };
            this.setState({
                activeChat: updated,
                chats: [updated, ...this.state.chats.filter(chat => chat.chatId !== updated.chatId)],
                messages: shown,
                isStreaming: false, streamingText: '', activity: undefined
            });
        } else {
            // A chat the api just created: the conversation carries on by id, and the list reads the
            // chat back as a resource, with the title the api wrote alongside the answer.
            this.pendingChatId = chatId;
            this.setState({ messages: shown, isStreaming: false, streamingText: '', activity: undefined });
            this.loadChats();
        }
        this.sendQueuedMessage();
    };

    private sendQueuedMessage = () => {
        const queued = this.queuedMessages.shift();
        if (queued) {
            this.sendMessage(queued);
        }
    };
}
